import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { LayerTable } from '../../components/map/LayerTable';
import { PrimaryNavRail } from '../../components/map/PrimaryNavRail';
import { api, ApiError } from '../../lib/api';
import { layersApi, type LayerFeature } from '../../lib/layersApi';
import type { Me } from '../../lib/types';

function initialsOf(me: Me | null): string {
  if (!me) return '…';
  const a = me.first_name?.[0] ?? '';
  const b = me.last_name?.[0] ?? '';
  return (a + b).toUpperCase() || '??';
}

function pickTitle(feature: LayerFeature): string {
  const firstString = Object.values(feature.properties).find((v): v is string => typeof v === 'string' && v.length > 0);
  return firstString ?? 'Объект';
}

export function LayerRegistryTablePage() {
  const { layerId } = useParams<{ layerId: string }>();
  const navigate = useNavigate();
  const [me, setMe] = useState<Me | null>(null);
  const [layerName, setLayerName] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    api.getMe().then(setMe).catch(() => {});
  }, []);

  useEffect(() => {
    if (!layerId) return;
    layersApi
      .getLayer(layerId)
      .then((detail) => setLayerName(detail.name))
      .catch((err) => setLoadError(err instanceof ApiError ? err.message : 'Не удалось загрузить слой.'));
  }, [layerId]);

  const handleGoToObject = (feature: LayerFeature) => {
    if (!feature.geometry) return;
    navigate('/app', {
      state: {
        focusGeometry: feature.geometry,
        focusProperties: { name: pickTitle(feature), objectType: layerName ?? undefined, ...feature.properties },
      },
    });
  };

  if (!layerId) return null;

  return (
    <div className="flex h-screen w-full overflow-hidden bg-bg-canvas">
      <PrimaryNavRail userInitials={initialsOf(me)} userAvatarUrl={me?.avatar_url} />

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <header className="flex h-[72px] shrink-0 items-center gap-3 border-b border-border-default bg-bg-surface px-8">
          <Link to="/app/data" className="text-sm font-medium text-text-secondary hover:text-text-primary">
            ← Данные и слои
          </Link>
          <span className="text-text-secondary">/</span>
          <p className="truncate text-sm font-semibold text-text-primary">{layerName ?? 'Таблица слоя'}</p>
          {loadError && <p className="text-xs font-medium text-status-danger">{loadError}</p>}
        </header>

        <div className="flex-1 overflow-hidden p-8">
          <div className="h-full overflow-hidden rounded-2xl border border-border-default bg-bg-surface">
            <LayerTable layerId={layerId} onGoToObject={handleGoToObject} />
          </div>
        </div>
      </div>
    </div>
  );
}
